import React, { useEffect, useState } from 'react'
import { collectionsApi } from '../../services/collectionsApi'
import LocationPickerModal, {
  PlaceEditPayload,
} from '../checkin/LocationPickerModal'

interface Props {
  collectionId: number
  itemId: number
  onClose: () => void
  onSuccess?: () => void
}

interface PlaceData {
  name?: string | null
  address?: string | null
  lat?: number | null
  lng?: number | null
  category_id?: number | null
  [k: string]: any
}

const EditPlaceById: React.FC<Props> = ({
  collectionId,
  itemId,
  onClose,
  onSuccess,
}) => {
  const [data, setData] = useState<PlaceData | null>(null)

  useEffect(() => {
    let cancelled = false
    collectionsApi.items.get(collectionId, itemId).then((item) => {
      if (!cancelled) setData(item?.data || {})
    }).catch((err) => {
      console.error('Failed to load place for edit:', err)
    })
    return () => { cancelled = true }
  }, [collectionId, itemId])

  if (!data) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-6">
          <div className="animate-spin h-8 w-8 border-4 border-amber-600 border-t-transparent rounded-full" />
        </div>
      </div>
    )
  }

  // Mirrors PlacesCollectionPage.handleEditSave: re-fetch + merge so we don't
  // drop fields the picker doesn't surface (source, creation_source, etc.).
  const handleEditSave = async (fields: PlaceEditPayload) => {
    const existing = await collectionsApi.items.get(collectionId, itemId)
    const existingData = existing?.data || {}
    const merged = {
      ...existingData,
      name: fields.name,
      lat: fields.lat,
      lng: fields.lng,
      address: fields.formattedAddress ?? existingData.address ?? null,
      google_place_id: fields.googlePlaceId,
      category_id: fields.categoryId,
      notes: fields.notes ?? null,
    }
    await collectionsApi.items.update(collectionId, itemId, { data: merged })
  }

  return (
    <LocationPickerModal
      open
      initial={{
        kind: 'place',
        collectionItemId: itemId,
        lat: data.lat ?? null,
        lng: data.lng ?? null,
        name: data.name ?? null,
        existingAddress: data.address ?? null,
        categoryId: data.category_id ?? null,
        notes: data.notes ?? null,
      }}
      editingPlaceId={itemId}
      onEditSave={handleEditSave}
      onConfirm={() => {
        onSuccess?.()
      }}
      onClose={onClose}
    />
  )
}

export default EditPlaceById
